import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import MenuNavBar from './MenuNavBar'
import {
  Box,
  Fade,
  Stack,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material'
import Image from 'next/image'
import { capitalise } from '@/helpers/strings'
import { useTranslation } from 'react-i18next'

const canteenMenuHeaderUserDataFragment = graphql(`
  fragment CanteenMenuHeader_UserDataFragment on User {
    ...CanteenMenuNavBar_UserDataFragment
    firstName
    lastName
  }
`)

const canteenMenuHeaderCurrentMenuDataFragment = graphql(`
  fragment CanteenMenuHeader_CurrentMenuDataFragment on CCMealService {
    ...CanteenMenuNavBar_CurrentMenuDataFragment
    timeslots {
      id
      vendorMenus {
        id
        vendor {
          id
          logo
          companyName
          descriptionFull
          images {
            secureUrl
          }
          translations {
            key
            value
            language
          }
        }
      }
    }
  }
`)

type Props = {
  userData: FragmentType<typeof canteenMenuHeaderUserDataFragment>
  menuData?: FragmentType<typeof canteenMenuHeaderCurrentMenuDataFragment> | null
  arrivalTime?: string | null
  currency: string
  isClosed: boolean
  meal: string
  date: string
  openingTime?: string
  orderTimeRemaining: moment.Duration
  onChangeSelectedService: (input: { meal: string; date: string }) => void
  onChangeAccountLocation: (accountLocationId: number) => void
  onChangeTimeslot: (timeslot: string) => void
}

const MenuHeader: React.FC<Props> = ({
  userData: userDataProp,
  menuData: menuDataProp,
  arrivalTime,
  isClosed,
  currency,
  meal,
  date,
  openingTime,
  orderTimeRemaining,
  onChangeSelectedService,
  onChangeAccountLocation,
  onChangeTimeslot,
}) => {
  const userData = useFragment(canteenMenuHeaderUserDataFragment, userDataProp)
  const menuData = useFragment(canteenMenuHeaderCurrentMenuDataFragment, menuDataProp)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()
  const [visibleImage, setVisibleImage] = useState(0)

  const vendorData =
    menuData?.timeslots[0]?.vendorMenus?.map((menu) => ({
      companyName: menu.vendor.companyName,
      logo: menu.vendor.logo,
      background: menu.vendor.images?.[0]?.secureUrl,
      tooltipText: menu.vendor.descriptionFull,
    })) ?? []

  const validImages =
    vendorData?.reduce<string[]>((images, vendor) => {
      return vendor.background ? [...images, vendor.background] : [...images]
    }, []) ?? []

  useEffect(() => {
    const showNextImage = () => {
      setVisibleImage((prev) => (prev === validImages?.length - 1 ? 0 : prev + 1))
    }

    const interval = setInterval(() => {
      showNextImage()
    }, 10000)

    return () => clearInterval(interval)
  }, [setVisibleImage, validImages.length])

  return (
    <>
      {!isMobile ? (
        <Box
          sx={{
            width: '100%',
            height: '230px',
            position: 'relative',
            backgroundColor: theme.palette.primary.main,
          }}
        >
          {validImages.map((image, index) => (
            <Fade appear={false} timeout={1000} key={image} in={index === visibleImage}>
              <Image
                style={{ filter: 'brightness(50%)' }}
                objectFit="cover"
                alt="image"
                src={image}
                layout="fill"
              />
            </Fade>
          ))}
          <Stack
            justifyContent="center"
            height="100%"
            zIndex={1}
            position="relative"
            padding={5}
            direction="column"
            spacing={3}
          >
            <Typography color="inverted.main" variant="h1">
              {capitalise(userData.firstName)},{' '}
              {isClosed
                ? 'this service is closed for orders'
                : t('cc:whatDoYouFancyForMeal', { context: meal, defaultValue: meal })}
            </Typography>
            <Stack minHeight="50px" direction="row" alignItems="center" alignContent="center">
              <Typography color="inverted.main" variant="h4">
                {vendorData?.length > 0
                  ? t('cc:handPickedRestaurants', { count: vendorData?.length })
                  : ''}
              </Typography>
              {vendorData?.length > 0 ? (
                <Stack marginLeft="auto" alignItems="center" spacing={1} direction="row">
                  <Typography color="inverted.main" variant="h6">
                    {t('cc:foodPreparedBy')}
                  </Typography>
                  {vendorData?.map((vendor) =>
                    vendor.logo ? (
                      <Tooltip key={vendor.companyName} title={vendor.companyName}>
                        <Avatar
                          sx={{ width: 50, height: 50 }}
                          alt={vendor.companyName}
                          src={vendor.logo}
                        />
                      </Tooltip>
                    ) : null,
                  )}
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        </Box>
      ) : null}

      <MenuNavBar
        onChangeSelectedService={onChangeSelectedService}
        onChangeAccountLocation={onChangeAccountLocation}
        currency={currency}
        userData={userData}
        menuData={menuData}
        openingTime={openingTime}
        date={date}
        meal={meal}
        onChangeTimeslot={onChangeTimeslot}
        arrivalTime={arrivalTime}
        orderTimeRemaining={orderTimeRemaining}
        menuClosed={isClosed}
      />
    </>
  )
}

export default MenuHeader
